import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/integrations/supabase/client";

export type LanguageCode = "en" | "ta" | "hi" | "te";

export const LANGUAGES: { code: LanguageCode; label: string; english: string }[] = [
  { code: "en", label: "English", english: "English" },
  { code: "ta", label: "தமிழ்", english: "Tamil" },
  { code: "hi", label: "हिन्दी", english: "Hindi" },
  { code: "te", label: "తెలుగు", english: "Telugu" },
];

export type LanguageToggles = Record<LanguageCode, boolean>;

export const DEFAULT_LANGUAGE_TOGGLES: LanguageToggles = {
  en: true,
  ta: true,
  hi: true,
  te: true,
};

const STORAGE_KEY = "landoo:language";

/* ------------------------------------------------------------------ */
/* Dictionaries — UI chrome only. Never used for user-entered content. */
/* ------------------------------------------------------------------ */

const en = {
  /* nav / menus */
  "nav.map": "Map",
  "nav.search": "Search",
  "nav.favourites": "Favourites",
  "nav.enquiries": "Enquiries",
  "nav.profile": "Profile",
  "nav.dashboard": "Dashboard",
  "nav.notifications": "Notifications",
  "nav.settings": "Settings",
  "nav.support": "Support",
  "nav.listings": "My Listings",
  "nav.subscription": "My Subscription",
  "nav.verification": "Verification",
  "nav.reports": "Reports",
  "nav.language": "Language",
  "nav.languages": "Languages",

  /* buttons */
  "btn.save": "Save",
  "btn.cancel": "Cancel",
  "btn.submit": "Submit",
  "btn.close": "Close",
  "btn.delete": "Delete",
  "btn.edit": "Edit",
  "btn.view": "View",
  "btn.share": "Share",
  "btn.copyLink": "Copy link",
  "btn.apply": "Apply",
  "btn.reset": "Reset",
  "btn.login": "Log in",
  "btn.logout": "Log out",
  "btn.register": "Register",
  "btn.listProperty": "List Your Property",
  "btn.locateMe": "Locate me",
  "btn.sendEnquiry": "Send Enquiry",
  "btn.requestContact": "Request Contact",
  "btn.requestVisit": "Request Visit",
  "btn.compare": "Add to Compare",
  "btn.tryAgain": "Try again",
  "btn.goHome": "Go home",

  /* labels */
  "label.price": "Price",
  "label.city": "City",
  "label.area": "Area",
  "label.type": "Property Type",
  "label.purpose": "Purpose",
  "label.distance": "Distance",
  "label.status": "Status",
  "label.date": "Date",
  "label.name": "Name",
  "label.email": "Email",
  "label.mobile": "Mobile",
  "label.message": "Message",
  "label.filters": "Filters",
  "label.sale": "Sale",
  "label.rent": "Rent",
  "label.lease": "Lease",
  "label.parking": "Parking",

  /* messages */
  "msg.loading": "Loading…",
  "msg.noResults": "No results found",
  "msg.saved": "Saved successfully",
  "msg.updated": "Updated successfully",
  "msg.deleted": "Deleted successfully",
  "msg.error": "Something went wrong. Please try again.",
  "msg.copied": "Link copied to clipboard",
  "msg.signInRequired": "Please sign in to continue",
  "msg.serviceInactive": "This service is not activated.",

  /* validation */
  "valid.required": "This field is required",
  "valid.email": "Enter a valid email address",
  "valid.mobile": "Enter a valid 10-digit mobile number",
  "valid.min": "Value is too short",
  "valid.max": "Value is too long",
  "valid.number": "Enter a valid number",
  "valid.date": "Enter a valid date",

  /* notifications */
  "notif.title": "Notifications",
  "notif.empty": "You have no notifications yet",
  "notif.markAllRead": "Mark all as read",
  "notif.newEnquiry": "New enquiry received",
  "notif.listingApproved": "Your listing was approved",
  "notif.listingRejected": "Your listing was rejected",
  "notif.contactRequest": "New contact request",
  "notif.visitUpdate": "Visit request updated",
  "notif.verificationUpdate": "Verification status updated",

  /* language admin */
  "lang.title": "Languages",
  "lang.subtitle": "Enable or disable the languages available in the language selector.",
  "lang.enabled": "Enabled",
  "lang.disabled": "Disabled",
  "lang.defaultLocked": "English is the default language and cannot be disabled.",
  "lang.changed": "Language updated",
} as const;

export type TranslationKey = keyof typeof en;
type Dict = Record<TranslationKey, string>;

const ta: Dict = {
  "nav.map": "வரைபடம்",
  "nav.search": "தேடல்",
  "nav.favourites": "பிடித்தவை",
  "nav.enquiries": "விசாரணைகள்",
  "nav.profile": "சுயவிவரம்",
  "nav.dashboard": "டாஷ்போர்டு",
  "nav.notifications": "அறிவிப்புகள்",
  "nav.settings": "அமைப்புகள்",
  "nav.support": "ஆதரவு",
  "nav.listings": "எனது பட்டியல்கள்",
  "nav.subscription": "எனது சந்தா",
  "nav.verification": "சரிபார்ப்பு",
  "nav.reports": "அறிக்கைகள்",
  "nav.language": "மொழி",
  "nav.languages": "மொழிகள்",

  "btn.save": "சேமி",
  "btn.cancel": "ரத்து செய்",
  "btn.submit": "சமர்ப்பி",
  "btn.close": "மூடு",
  "btn.delete": "நீக்கு",
  "btn.edit": "திருத்து",
  "btn.view": "பார்",
  "btn.share": "பகிர்",
  "btn.copyLink": "இணைப்பை நகலெடு",
  "btn.apply": "பயன்படுத்து",
  "btn.reset": "மீட்டமை",
  "btn.login": "உள்நுழை",
  "btn.logout": "வெளியேறு",
  "btn.register": "பதிவு செய்",
  "btn.listProperty": "உங்கள் சொத்தை பட்டியலிடுங்கள்",
  "btn.locateMe": "என் இடத்தைக் கண்டறி",
  "btn.sendEnquiry": "விசாரணை அனுப்பு",
  "btn.requestContact": "தொடர்பு கோரு",
  "btn.requestVisit": "பார்வையிட கோரு",
  "btn.compare": "ஒப்பிட சேர்",
  "btn.tryAgain": "மீண்டும் முயற்சி",
  "btn.goHome": "முகப்புக்கு செல்",

  "label.price": "விலை",
  "label.city": "நகரம்",
  "label.area": "பகுதி",
  "label.type": "சொத்து வகை",
  "label.purpose": "நோக்கம்",
  "label.distance": "தூரம்",
  "label.status": "நிலை",
  "label.date": "தேதி",
  "label.name": "பெயர்",
  "label.email": "மின்னஞ்சல்",
  "label.mobile": "கைபேசி",
  "label.message": "செய்தி",
  "label.filters": "வடிகட்டிகள்",
  "label.sale": "விற்பனை",
  "label.rent": "வாடகை",
  "label.lease": "குத்தகை",
  "label.parking": "வாகன நிறுத்தம்",

  "msg.loading": "ஏற்றுகிறது…",
  "msg.noResults": "முடிவுகள் இல்லை",
  "msg.saved": "வெற்றிகரமாக சேமிக்கப்பட்டது",
  "msg.updated": "வெற்றிகரமாக புதுப்பிக்கப்பட்டது",
  "msg.deleted": "வெற்றிகரமாக நீக்கப்பட்டது",
  "msg.error": "ஏதோ தவறு நடந்தது. மீண்டும் முயற்சிக்கவும்.",
  "msg.copied": "இணைப்பு நகலெடுக்கப்பட்டது",
  "msg.signInRequired": "தொடர உள்நுழையவும்",
  "msg.serviceInactive": "இந்த சேவை செயல்படுத்தப்படவில்லை.",

  "valid.required": "இந்த புலம் அவசியம்",
  "valid.email": "சரியான மின்னஞ்சலை உள்ளிடவும்",
  "valid.mobile": "சரியான 10 இலக்க கைபேசி எண்ணை உள்ளிடவும்",
  "valid.min": "மதிப்பு மிகக் குறைவு",
  "valid.max": "மதிப்பு மிக நீளம்",
  "valid.number": "சரியான எண்ணை உள்ளிடவும்",
  "valid.date": "சரியான தேதியை உள்ளிடவும்",

  "notif.title": "அறிவிப்புகள்",
  "notif.empty": "இதுவரை அறிவிப்புகள் இல்லை",
  "notif.markAllRead": "அனைத்தையும் படித்ததாகக் குறி",
  "notif.newEnquiry": "புதிய விசாரணை வந்துள்ளது",
  "notif.listingApproved": "உங்கள் பட்டியல் அங்கீகரிக்கப்பட்டது",
  "notif.listingRejected": "உங்கள் பட்டியல் நிராகரிக்கப்பட்டது",
  "notif.contactRequest": "புதிய தொடர்பு கோரிக்கை",
  "notif.visitUpdate": "பார்வை கோரிக்கை புதுப்பிக்கப்பட்டது",
  "notif.verificationUpdate": "சரிபார்ப்பு நிலை புதுப்பிக்கப்பட்டது",

  "lang.title": "மொழிகள்",
  "lang.subtitle": "மொழி தேர்வில் கிடைக்கும் மொழிகளை இயக்கவும் அல்லது முடக்கவும்.",
  "lang.enabled": "இயக்கப்பட்டது",
  "lang.disabled": "முடக்கப்பட்டது",
  "lang.defaultLocked": "ஆங்கிலம் இயல்பு மொழி; முடக்க முடியாது.",
  "lang.changed": "மொழி புதுப்பிக்கப்பட்டது",
};

const hi: Dict = {
  "nav.map": "मानचित्र",
  "nav.search": "खोज",
  "nav.favourites": "पसंदीदा",
  "nav.enquiries": "पूछताछ",
  "nav.profile": "प्रोफ़ाइल",
  "nav.dashboard": "डैशबोर्ड",
  "nav.notifications": "सूचनाएं",
  "nav.settings": "सेटिंग्स",
  "nav.support": "सहायता",
  "nav.listings": "मेरी लिस्टिंग",
  "nav.subscription": "मेरी सदस्यता",
  "nav.verification": "सत्यापन",
  "nav.reports": "रिपोर्ट",
  "nav.language": "भाषा",
  "nav.languages": "भाषाएँ",

  "btn.save": "सहेजें",
  "btn.cancel": "रद्द करें",
  "btn.submit": "जमा करें",
  "btn.close": "बंद करें",
  "btn.delete": "हटाएं",
  "btn.edit": "संपादित करें",
  "btn.view": "देखें",
  "btn.share": "साझा करें",
  "btn.copyLink": "लिंक कॉपी करें",
  "btn.apply": "लागू करें",
  "btn.reset": "रीसेट",
  "btn.login": "लॉग इन",
  "btn.logout": "लॉग आउट",
  "btn.register": "रजिस्टर करें",
  "btn.listProperty": "अपनी संपत्ति सूचीबद्ध करें",
  "btn.locateMe": "मुझे ढूंढें",
  "btn.sendEnquiry": "पूछताछ भेजें",
  "btn.requestContact": "संपर्क अनुरोध",
  "btn.requestVisit": "विज़िट अनुरोध",
  "btn.compare": "तुलना में जोड़ें",
  "btn.tryAgain": "पुनः प्रयास करें",
  "btn.goHome": "होम पर जाएं",

  "label.price": "कीमत",
  "label.city": "शहर",
  "label.area": "क्षेत्र",
  "label.type": "संपत्ति प्रकार",
  "label.purpose": "उद्देश्य",
  "label.distance": "दूरी",
  "label.status": "स्थिति",
  "label.date": "तारीख",
  "label.name": "नाम",
  "label.email": "ईमेल",
  "label.mobile": "मोबाइल",
  "label.message": "संदेश",
  "label.filters": "फ़िल्टर",
  "label.sale": "बिक्री",
  "label.rent": "किराया",
  "label.lease": "पट्टा",
  "label.parking": "पार्किंग",

  "msg.loading": "लोड हो रहा है…",
  "msg.noResults": "कोई परिणाम नहीं मिला",
  "msg.saved": "सफलतापूर्वक सहेजा गया",
  "msg.updated": "सफलतापूर्वक अपडेट किया गया",
  "msg.deleted": "सफलतापूर्वक हटाया गया",
  "msg.error": "कुछ गलत हो गया। कृपया पुनः प्रयास करें।",
  "msg.copied": "लिंक कॉपी हो गया",
  "msg.signInRequired": "जारी रखने के लिए साइन इन करें",
  "msg.serviceInactive": "यह सेवा सक्रिय नहीं है।",

  "valid.required": "यह फ़ील्ड आवश्यक है",
  "valid.email": "मान्य ईमेल पता दर्ज करें",
  "valid.mobile": "मान्य 10 अंकों का मोबाइल नंबर दर्ज करें",
  "valid.min": "मान बहुत छोटा है",
  "valid.max": "मान बहुत लंबा है",
  "valid.number": "मान्य संख्या दर्ज करें",
  "valid.date": "मान्य तारीख दर्ज करें",

  "notif.title": "सूचनाएं",
  "notif.empty": "अभी कोई सूचना नहीं है",
  "notif.markAllRead": "सभी को पढ़ा हुआ चिह्नित करें",
  "notif.newEnquiry": "नई पूछताछ प्राप्त हुई",
  "notif.listingApproved": "आपकी लिस्टिंग स्वीकृत हुई",
  "notif.listingRejected": "आपकी लिस्टिंग अस्वीकृत हुई",
  "notif.contactRequest": "नया संपर्क अनुरोध",
  "notif.visitUpdate": "विज़िट अनुरोध अपडेट हुआ",
  "notif.verificationUpdate": "सत्यापन स्थिति अपडेट हुई",

  "lang.title": "भाषाएँ",
  "lang.subtitle": "भाषा चयनकर्ता में उपलब्ध भाषाओं को सक्षम या अक्षम करें।",
  "lang.enabled": "सक्षम",
  "lang.disabled": "अक्षम",
  "lang.defaultLocked": "अंग्रेज़ी डिफ़ॉल्ट भाषा है और अक्षम नहीं की जा सकती।",
  "lang.changed": "भाषा अपडेट हुई",
};

const te: Dict = {
  "nav.map": "మ్యాప్",
  "nav.search": "శోధన",
  "nav.favourites": "ఇష్టమైనవి",
  "nav.enquiries": "విచారణలు",
  "nav.profile": "ప్రొఫైల్",
  "nav.dashboard": "డాష్‌బోర్డ్",
  "nav.notifications": "నోటిఫికేషన్లు",
  "nav.settings": "సెట్టింగ్‌లు",
  "nav.support": "సహాయం",
  "nav.listings": "నా లిస్టింగ్‌లు",
  "nav.subscription": "నా సబ్‌స్క్రిప్షన్",
  "nav.verification": "ధృవీకరణ",
  "nav.reports": "నివేదికలు",
  "nav.language": "భాష",
  "nav.languages": "భాషలు",

  "btn.save": "సేవ్ చేయి",
  "btn.cancel": "రద్దు",
  "btn.submit": "సమర్పించు",
  "btn.close": "మూసివేయి",
  "btn.delete": "తొలగించు",
  "btn.edit": "సవరించు",
  "btn.view": "చూడు",
  "btn.share": "షేర్ చేయి",
  "btn.copyLink": "లింక్ కాపీ చేయి",
  "btn.apply": "వర్తించు",
  "btn.reset": "రీసెట్",
  "btn.login": "లాగిన్",
  "btn.logout": "లాగ్ అవుట్",
  "btn.register": "నమోదు",
  "btn.listProperty": "మీ ఆస్తిని జాబితా చేయండి",
  "btn.locateMe": "నా స్థానం",
  "btn.sendEnquiry": "విచారణ పంపు",
  "btn.requestContact": "సంప్రదింపు అభ్యర్థన",
  "btn.requestVisit": "సందర్శన అభ్యర్థన",
  "btn.compare": "పోలికకు జోడించు",
  "btn.tryAgain": "మళ్లీ ప్రయత్నించు",
  "btn.goHome": "హోమ్‌కి వెళ్లు",

  "label.price": "ధర",
  "label.city": "నగరం",
  "label.area": "ప్రాంతం",
  "label.type": "ఆస్తి రకం",
  "label.purpose": "ప్రయోజనం",
  "label.distance": "దూరం",
  "label.status": "స్థితి",
  "label.date": "తేదీ",
  "label.name": "పేరు",
  "label.email": "ఇమెయిల్",
  "label.mobile": "మొబైల్",
  "label.message": "సందేశం",
  "label.filters": "ఫిల్టర్లు",
  "label.sale": "అమ్మకం",
  "label.rent": "అద్దె",
  "label.lease": "లీజు",
  "label.parking": "పార్కింగ్",

  "msg.loading": "లోడ్ అవుతోంది…",
  "msg.noResults": "ఫలితాలు లేవు",
  "msg.saved": "విజయవంతంగా సేవ్ అయింది",
  "msg.updated": "విజయవంతంగా నవీకరించబడింది",
  "msg.deleted": "విజయవంతంగా తొలగించబడింది",
  "msg.error": "ఏదో తప్పు జరిగింది. మళ్లీ ప్రయత్నించండి.",
  "msg.copied": "లింక్ కాపీ చేయబడింది",
  "msg.signInRequired": "కొనసాగడానికి సైన్ ఇన్ చేయండి",
  "msg.serviceInactive": "ఈ సేవ యాక్టివేట్ చేయబడలేదు.",

  "valid.required": "ఈ ఫీల్డ్ తప్పనిసరి",
  "valid.email": "సరైన ఇమెయిల్ చిరునామా నమోదు చేయండి",
  "valid.mobile": "సరైన 10 అంకెల మొబైల్ నంబర్ నమోదు చేయండి",
  "valid.min": "విలువ చాలా చిన్నది",
  "valid.max": "విలువ చాలా పొడవుగా ఉంది",
  "valid.number": "సరైన సంఖ్యను నమోదు చేయండి",
  "valid.date": "సరైన తేదీని నమోదు చేయండి",

  "notif.title": "నోటిఫికేషన్లు",
  "notif.empty": "ఇంకా నోటిఫికేషన్లు లేవు",
  "notif.markAllRead": "అన్నీ చదివినట్లు గుర్తించు",
  "notif.newEnquiry": "కొత్త విచారణ వచ్చింది",
  "notif.listingApproved": "మీ లిస్టింగ్ ఆమోదించబడింది",
  "notif.listingRejected": "మీ లిస్టింగ్ తిరస్కరించబడింది",
  "notif.contactRequest": "కొత్త సంప్రదింపు అభ్యర్థన",
  "notif.visitUpdate": "సందర్శన అభ్యర్థన నవీకరించబడింది",
  "notif.verificationUpdate": "ధృవీకరణ స్థితి నవీకరించబడింది",

  "lang.title": "భాషలు",
  "lang.subtitle": "భాషా ఎంపికలో అందుబాటులో ఉన్న భాషలను ప్రారంభించండి లేదా ఆపివేయండి.",
  "lang.enabled": "ప్రారంభించబడింది",
  "lang.disabled": "ఆపివేయబడింది",
  "lang.defaultLocked": "ఇంగ్లీష్ డిఫాల్ట్ భాష, దీన్ని ఆపివేయలేరు.",
  "lang.changed": "భాష నవీకరించబడింది",
};

const DICTS: Record<LanguageCode, Dict> = { en: en as unknown as Dict, ta, hi, te };

/* ---------------- settings helpers ---------------- */

export async function fetchLanguageToggles(): Promise<LanguageToggles> {
  const { data, error } = await supabase
    .from("platform_settings")
    .select("value")
    .eq("key", "languages")
    .maybeSingle();
  if (error) throw error;
  return { ...DEFAULT_LANGUAGE_TOGGLES, ...((data?.value as Partial<LanguageToggles>) ?? {}) };
}

export async function saveLanguageToggles(next: LanguageToggles): Promise<void> {
  const { error } = await supabase
    .from("platform_settings")
    .update({ value: { ...next, en: true } as never })
    .eq("key", "languages");
  if (error) throw error;
}

/* ---------------- context ---------------- */

interface I18nCtx {
  language: LanguageCode;
  setLanguage: (code: LanguageCode) => void;
  t: (key: TranslationKey, fallback?: string) => string;
  enabled: LanguageToggles;
  availableLanguages: typeof LANGUAGES;
}

const I18nContext = createContext<I18nCtx | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>("en");
  const [enabled, setEnabled] = useState<LanguageToggles>(DEFAULT_LANGUAGE_TOGGLES);

  // Read persisted choice after hydration to avoid SSR mismatch.
  useEffect(() => {
    // `?lang=xx` (the hreflang variant URL) wins over the stored choice.
    const param = new URLSearchParams(window.location.search).get("lang") as LanguageCode | null;
    if (param && param in DICTS) {
      setLanguageState(param);
      return;
    }
    const stored = window.localStorage.getItem(STORAGE_KEY) as LanguageCode | null;
    if (stored && stored in DICTS) setLanguageState(stored);
  }, []);

  useEffect(() => {
    let active = true;
    fetchLanguageToggles()
      .then((v) => active && setEnabled(v))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  // Fall back to English if the current language gets disabled.
  useEffect(() => {
    if (!enabled[language]) setLanguageState("en");
  }, [enabled, language]);

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((code: LanguageCode) => {
    setLanguageState(code);
    try {
      window.localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* storage unavailable */
    }
  }, []);

  const t = useCallback(
    (key: TranslationKey, fallback?: string) =>
      DICTS[language][key] ?? (en as Dict)[key] ?? fallback ?? key,
    [language],
  );

  const availableLanguages = useMemo(
    () => LANGUAGES.filter((l) => enabled[l.code] || l.code === "en"),
    [enabled],
  );

  const value = useMemo(
    () => ({ language, setLanguage, t, enabled, availableLanguages }),
    [language, setLanguage, t, enabled, availableLanguages],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nCtx {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}

export function useT() {
  return useI18n().t;
}
